import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import { requestLoginCode, requestPasswordReset, googleAuthConfig } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Mail, Lock, User, Phone, KeyRound, ArrowLeft } from "lucide-react";

const GOOGLE_CONSENT_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const FIELD = "w-full rounded-xl border border-[var(--n-300)] pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]";

export default function AuthModal({ open, onClose, onSuccess, title = "Accedi al tuo account" }) {
  const { login, register, loginWithCode } = useAuth();
  const [mode, setMode] = useState("login");   // login | register | code | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", code: "" });
  const [codeSent, setCodeSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [busy, setBusy] = useState(false);

  /* iter414 · Finestra d'accesso: blocca lo scorrimento della pagina dietro
     (su mobile il sito scorreva sotto al modale) e chiude con Esc. */
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
  const switchMode = (m) => { setMode(m); setCodeSent(false); setResetSent(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register") {
      const digits = (form.phone || "").replace(/[^\d+]/g, "");
      if (digits.length < 7) { toast.error("Inserisci un numero di telefono valido (WhatsApp/mobile)"); return; }
    }
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ name: form.name, email: form.email, password: form.password, phone: form.phone });
      toast.success(mode === "login" ? "Bentornato!" : "Account creato!");
      onSuccess?.(); onClose?.();
    } catch (e2) { err(e2, "Operazione non riuscita"); } finally { setBusy(false); }
  };

  const sendCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await requestLoginCode(form.email);
      setCodeSent(true);
      toast.success("Ti abbiamo inviato un codice a 6 cifre. Controlla la posta.");
    } catch (e2) { err(e2, "Invio non riuscito"); } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginWithCode(form.email, form.code.trim());
      toast.success("Bentornato!");
      onSuccess?.(); onClose?.();
    } catch (e2) { err(e2, "Codice non valido"); } finally { setBusy(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestPasswordReset(form.email);
      setResetSent(true);
    } catch (e2) { err(e2, "Invio non riuscito"); } finally { setBusy(false); }
  };

  // Consenso Google intestato a Squiddi (client Google Cloud nostro).
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const google = async () => {
    setBusy(true);
    try {
      const cfg = await googleAuthConfig();
      if (!cfg?.enabled || !cfg?.client_id) throw new Error("google-off");
      const state = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      sessionStorage.setItem("google_oauth_state", state);
      localStorage.setItem("post_login_redirect", window.location.pathname + window.location.search);
      const params = new URLSearchParams({
        client_id: cfg.client_id,
        redirect_uri: `${window.location.origin}/auth/google`,
        response_type: "code",
        scope: "openid email profile",
        state,
        access_type: "online",
        prompt: "select_account",
      });
      window.location.href = `${GOOGLE_CONSENT_URL}?${params.toString()}`;
    } catch {
      setBusy(false);
      toast.error("Accesso Google non disponibile in questo momento: usa email e password oppure il codice via email.");
    }
  };

  const isPwd = mode === "login" || mode === "register";

  return (
  /* iter414 · Portale su <body>: dentro l'header (sticky z-50) la finestra
     finiva sotto alla barra cookie e agli altri livelli del sito. */
  createPortal((
    <div className="fixed inset-0 z-[200] bg-[var(--brand-ink)]/60 backdrop-blur-md overflow-y-auto overscroll-contain flex items-start sm:items-center justify-center p-3 sm:p-5" data-testid="auth-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-md my-auto rounded-[1.6rem] overflow-hidden shadow-2xl ring-1 ring-black/5 flex flex-col max-h-[calc(100dvh-1.5rem)]" data-testid="auth-panel" onClick={(e) => e.stopPropagation()}>
        <div className="h-1.5 w-full shrink-0 bg-[linear-gradient(90deg,#8C52FF_0%,#6C3DEE_50%,#FFBC7D_100%)]" />
        <div className="p-5 sm:p-6 overflow-y-auto" data-testid="auth-scroll">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display font-extrabold text-lg sm:text-xl leading-tight">{title}</h3>
              <p className="text-[12.5px] text-[var(--n-600)] mt-0.5">Salva ordini e progetti su ogni dispositivo.</p>
            </div>
            <button onClick={onClose} data-testid="auth-close" className="grid place-items-center w-9 h-9 rounded-xl border border-[var(--n-300)] text-[var(--n-600)] hover:text-[var(--brand)]"><X size={18} /></button>
          </div>

          {isPwd && (
            <>
              <div className="mt-4 inline-flex p-1 rounded-full bg-[var(--n-100)] border border-[var(--n-300)] w-full">
                {[["login", "Accedi"], ["register", "Registrati"]].map(([m, l]) => (
                  <button key={m} onClick={() => switchMode(m)} data-testid={`auth-mode-${m}`}
                    className={`flex-1 py-2 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-white text-[var(--brand)] shadow-sm" : "text-[var(--n-600)]"}`}>{l}</button>
                ))}
              </div>

              <button onClick={google} disabled={busy} data-testid="auth-google" className="mt-3 w-full flex items-center justify-center gap-2.5 rounded-xl border-2 border-[var(--n-300)] py-2.5 font-bold hover:border-[var(--brand)] hover:bg-[var(--n-50)] transition-colors disabled:opacity-60">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continua con Google
              </button>
              <button onClick={() => switchMode("code")} data-testid="auth-use-code" className="mt-2.5 w-full flex items-center justify-center gap-2.5 rounded-xl border-2 border-[var(--ok-border)] bg-[var(--ok-surface)] py-2.5 font-bold text-[var(--ok-ink)] hover:border-[var(--ok-border-2)] transition-colors">
                <KeyRound size={17} className="text-[var(--ok-soft)]" /> Entra con un codice via email
              </button>

              <div className="flex items-center gap-3 my-3">
                <span className="flex-1 h-px bg-[var(--n-200)]" /><span className="text-[11px] text-[var(--n-500)] font-semibold">oppure</span><span className="flex-1 h-px bg-[var(--n-200)]" />
              </div>

              <form onSubmit={submit} className="space-y-3">
                {mode === "register" && (
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-400)]" />
                    <input value={form.name} onChange={set("name")} data-testid="auth-name" placeholder="Nome e cognome" className={FIELD} />
                  </div>
                )}
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-400)]" />
                  <input type="email" required value={form.email} onChange={set("email")} data-testid="auth-email" placeholder="La tua email" className={FIELD} />
                </div>
                {mode === "register" && (
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-400)]" />
                    <input type="tel" required value={form.phone} onChange={set("phone")} data-testid="auth-phone"
                      placeholder="Telefono / WhatsApp (es. +39 340 1234567)" className={FIELD} />
                  </div>
                )}
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-400)]" />
                  <input type="password" required value={form.password} onChange={set("password")} data-testid="auth-password" placeholder="Password (min 6 caratteri)" className={FIELD} />
                </div>
                {mode === "register" && (
                  <p className="text-[10.5px] text-[var(--n-500)] leading-relaxed">
                    Usiamo il numero per contattarti su WhatsApp con anteprime e aggiornamenti sull&apos;ordine. Nessun spam.
                  </p>
                )}
                <button disabled={busy} data-testid="auth-submit" className="btn-primary w-full py-2.5">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : (mode === "login" ? "Accedi" : "Crea account")}
                </button>
              </form>
              {mode === "login" && (
                <button onClick={() => switchMode("forgot")} data-testid="auth-forgot" className="mt-2.5 w-full text-center text-[12.5px] font-bold text-[var(--brand)] hover:underline">
                  Password dimenticata?
                </button>
              )}
            </>
          )}

          {mode === "code" && (
            <form onSubmit={codeSent ? verifyCode : sendCode} className="mt-5 space-y-3" data-testid="auth-code-form">
              <p className="text-sm text-[var(--n-700)]">
                {codeSent
                  ? <>Abbiamo inviato un codice a <b>{form.email}</b>. Scade tra 10 minuti.</>
                  : "Ti mandiamo un codice a 6 cifre: niente password da ricordare."}
              </p>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-400)]" />
                <input type="email" required value={form.email} onChange={set("email")} data-testid="auth-code-email"
                  placeholder="La tua email" disabled={codeSent} className={`${FIELD} disabled:bg-[var(--n-100)]`} />
              </div>
              {codeSent && (
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.replace(/\D/g, "") }))}
                  data-testid="auth-code-input" placeholder="000000"
                  className="w-full rounded-xl border-2 border-[var(--ok-border)] bg-[var(--ok-surface)] px-3 py-3 text-center text-2xl font-black tracking-[0.5em] text-[var(--ok-ink)] outline-none focus:border-[var(--brand)]" />
              )}
              <button disabled={busy} data-testid={codeSent ? "auth-code-verify" : "auth-code-send"} className="btn-primary w-full py-3">
                {busy ? <Loader2 className="animate-spin" size={18} /> : (codeSent ? "Entra" : "Inviami il codice")}
              </button>
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => switchMode("login")} data-testid="auth-back-password" className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[var(--n-600)] hover:text-[var(--brand)]">
                  <ArrowLeft size={13} /> Usa la password
                </button>
                {codeSent && (
                  <button type="button" onClick={sendCode} disabled={busy} data-testid="auth-code-resend" className="text-[12.5px] font-bold text-[var(--brand)] hover:underline">
                    Invia di nuovo
                  </button>
                )}
              </div>
            </form>
          )}

          {mode === "forgot" && (
            <div className="mt-5 space-y-3">
              {resetSent ? (
                <div className="rounded-2xl border-2 border-[var(--ok-border)] bg-[var(--ok-surface)] p-4 text-sm text-[var(--ok-ink)]" data-testid="auth-forgot-sent">
                  Se <b>{form.email}</b> è registrata, ti arriva subito un link per scegliere una nuova password. Il link vale 1 ora.
                </div>
              ) : (
                <form onSubmit={sendReset} className="space-y-3" data-testid="auth-forgot-form">
                  <p className="text-sm text-[var(--n-700)]">Inserisci la tua email: ti mandiamo un link per reimpostare la password.</p>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-400)]" />
                    <input type="email" required value={form.email} onChange={set("email")} data-testid="auth-forgot-email" placeholder="La tua email" className={FIELD} />
                  </div>
                  <button disabled={busy} data-testid="auth-forgot-send" className="btn-primary w-full py-3">
                    {busy ? <Loader2 className="animate-spin" size={18} /> : "Inviami il link"}
                  </button>
                </form>
              )}
              <button type="button" onClick={() => switchMode("login")} data-testid="auth-forgot-back" className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[var(--n-600)] hover:text-[var(--brand)]">
                <ArrowLeft size={13} /> Torna all&apos;accesso
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  ), document.body));
}
